/**
 * Open a signing document in a new tab, for the § 7001(c)(1)(C)(ii) access
 * demonstration.
 *
 * Extracted from `SigningPage` because the sequence has two traps that are
 * invisible on inspection and were both live in the first version — it needs
 * to be testable on its own rather than reachable only through a full page
 * render.
 *
 * # Trap 1: `noopener` makes `window.open` return null
 *
 * `window.open(url, '_blank', 'noopener')` returns **null**. That is what the
 * flag means — the handle is severed — and it is not an error. The original
 * code passed `'noopener,noreferrer'` while opening a BLANK tab it intended to
 * navigate afterwards, so the tab opened and nothing ever pointed it anywhere.
 * A Regulated signer, for whom this button is the only way past the consent
 * gate, was shown a permanently blank page as proof they could read a PDF.
 *
 * The isolation is restored by nulling `opener` by hand, which is equivalent
 * for a tab we are about to send to a presigned S3 URL and costs no handle.
 *
 * # Trap 2: the tab must be opened BEFORE the await
 *
 * The URL comes from the server, because issuing it is what the server records
 * as the demonstration. But calling `window.open` after an awaited request is
 * popup-blocked in Safari, and in Chrome once the user activation lapses. So
 * the tab is opened synchronously inside the click and navigated later.
 */

export interface OpenDocumentDeps {
  /** Injected for tests; defaults to the real one. */
  open?: typeof window.open;
  doc?: Document;
}

/**
 * Open `getUrl()`'s result in a new tab.
 *
 * `getUrl` is called AFTER the tab exists, so the blocking rule above holds no
 * matter how slow the server is. On failure the blank tab is closed — leaving
 * it behind is indistinguishable from the bug this replaced.
 */
export async function openDocumentInNewTab(
  getUrl: () => Promise<string>,
  deps: OpenDocumentDeps = {},
): Promise<void> {
  const openFn = deps.open ?? window.open.bind(window);
  const doc = deps.doc ?? document;

  // No `noopener` — see Trap 1. Without this the handle below is always null.
  const popup = openFn('', '_blank');
  if (popup) {
    try {
      popup.opener = null;
    } catch {
      // Read-only on a fresh about:blank in some browsers. Nothing is lost:
      // the destination is another origin and cannot reach back regardless.
    }
  }

  let url: string;
  try {
    url = await getUrl();
  } catch (err) {
    popup?.close();
    throw err;
  }

  if (popup && !popup.closed) {
    // Navigated, not downloaded: the browser's own PDF handling is precisely
    // the capability being demonstrated.
    popup.location.href = url;
    return;
  }

  // Genuinely blocked, or the signer closed the tab while the URL was in
  // flight. An anchor click is not intercepted by popup blockers.
  const a = doc.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  doc.body.appendChild(a);
  a.click();
  a.remove();
}
